import { useState, useMemo } from "react";
import { Check, Columns, X } from "lucide-react";
import { autoDetectField } from "../lib/spreadsheet";

export interface FieldDefinition {
  key: string;
  label: string;
  required?: boolean;
  hint?: string;
}

interface ColumnMappingModalProps {
  headers: string[];
  sampleRows: Record<string, string>[];
  fields: FieldDefinition[];
  onConfirm: (mapping: Record<string, string>) => void;
  onCancel: () => void;
}

export function ColumnMappingModal({
  headers,
  sampleRows,
  fields,
  onConfirm,
  onCancel,
}: ColumnMappingModalProps) {
  // Tự động suy đoán ban đầu
  const initialMapping = useMemo(() => {
    const map: Record<string, string> = {};
    for (const header of headers) {
      const detected = autoDetectField(header);
      if (detected && fields.some((f) => f.key === detected)) {
        map[detected] = header;
      }
    }
    return map;
  }, [headers, fields]);

  const [mapping, setMapping] = useState<Record<string, string>>(initialMapping);

  function handleSelect(fieldKey: string, header: string) {
    setMapping((prev) => {
      const next = { ...prev };

      if (!header) {
        delete next[fieldKey];
        return next;
      }

      // Một cột nguồn chỉ được map cho một trường đích.
      // Nếu người dùng chọn lại cùng header, tự động bỏ mapping cũ.
      for (const [key, value] of Object.entries(next)) {
        if (key !== fieldKey && value === header) {
          delete next[key];
        }
      }

      next[fieldKey] = header;
      return next;
    });
  }

  const missingRequired = useMemo(() => {
    return fields.filter((f) => f.required && !mapping[f.key]);
  }, [fields, mapping]);

  return (
    <div className="modal-backdrop" style={{
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(15, 23, 42, 0.65)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: "16px",
    }}>
      <div
        className="card form-stack"
        style={{
          width: "100%",
          maxWidth: "680px",
          maxHeight: "90vh",
          overflowY: "auto",
          backgroundColor: "#ffffff",
          borderRadius: "16px",
          boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
          padding: "24px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Columns size={22} color="#2563eb" />
            <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 700 }}>
              Ánh xạ cột dữ liệu từ tệp
            </h2>
          </div>
          <button
            type="button"
            className="secondary-button"
            onClick={onCancel}
            style={{ padding: "6px", borderRadius: "50%" }}
            title="Đóng"
          >
            <X size={18} />
          </button>
        </div>

        <p style={{ color: "#64748b", fontSize: "0.875rem", margin: 0 }}>
          Hệ thống đã tự động nhận dạng các cột tương đồng. Bạn hãy kiểm tra lại và điều chỉnh nếu cần:
        </p>

        <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "8px" }}>
          {fields.map((field) => {
            const currentHeader = mapping[field.key] || "";
            return (
              <div
                key={field.key}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1.5fr",
                  gap: "12px",
                  alignItems: "center",
                  padding: "10px 14px",
                  backgroundColor: currentHeader ? "#f8fafc" : "#fff7ed",
                  borderRadius: "10px",
                  border: currentHeader ? "1px solid #e2e8f0" : "1px solid #fed7aa",
                }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: "0.938rem", color: "#1e293b" }}>
                    {field.label}
                    {field.required ? (
                      <span style={{ color: "#ef4444", marginLeft: "4px" }}>*</span>
                    ) : null}
                  </div>
                  {field.hint ? (
                    <div style={{ fontSize: "0.75rem", color: "#64748b" }}>{field.hint}</div>
                  ) : null}
                </div>

                <select
                  value={currentHeader}
                  onChange={(e) => handleSelect(field.key, e.target.value)}
                  style={{
                    padding: "8px 12px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    backgroundColor: "#ffffff",
                    fontSize: "0.875rem",
                  }}
                >
                  <option value="">-- Không ánh xạ (Bỏ qua) --</option>
                  {headers.map((h) => (
                    <option key={h} value={h}>
                      Cột: {h}
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>

        {/* Xem trước mẫu dữ liệu dòng 1 */}
        {sampleRows.length > 0 ? (
          <div style={{ marginTop: "12px" }}>
            <div style={{ fontSize: "0.813rem", fontWeight: 600, color: "#475569", marginBottom: "6px" }}>
              Xem trước 1 dòng thực tế trong file:
            </div>
            <div
              style={{
                backgroundColor: "#f1f5f9",
                borderRadius: "8px",
                padding: "10px",
                fontSize: "0.813rem",
                overflowX: "auto",
                fontFamily: "monospace",
              }}
            >
              {Object.entries(sampleRows[0] || {})
                .slice(0, 6)
                .map(([k, v]) => (
                  <div key={k} style={{ whiteSpace: "nowrap" }}>
                    <strong style={{ color: "#0f172a" }}>{k}:</strong> {String(v)}
                  </div>
                ))}
            </div>
          </div>
        ) : null}

        {missingRequired.length > 0 ? (
          <div
            style={{
              padding: "10px 14px",
              borderRadius: "8px",
              backgroundColor: "#fee2e2",
              color: "#991b1b",
              fontSize: "0.875rem",
            }}
          >
            Chưa ánh xạ các cột bắt buộc: <strong>{missingRequired.map((f) => f.label).join(", ")}</strong>
          </div>
        ) : null}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "16px" }}>
          <button type="button" className="secondary-button" onClick={onCancel}>
            Hủy bỏ
          </button>
          <button
            type="button"
            className="primary-button"
            disabled={missingRequired.length > 0}
            onClick={() => onConfirm(mapping)}
            style={{ display: "flex", alignItems: "center", gap: "6px" }}
          >
            <Check size={16} />
            Đồng ý & Xem bảng kiểm tra
          </button>
        </div>
      </div>
    </div>
  );
}
